import { useState } from "react";

import { normalizeToDate } from "common/utils/dates";
import { Feature, SubscriptionFrequency, UserSubscriptionAction } from "common/types";

import DrawerLayout from "client/common/components/stackables/DrawerLayout";
import { DetailSubscription, ListSubscription } from "client/subscriptions/types";
import { useUpdateSubscriptionMutation } from "client/subscriptions/hooks";
import { useCurrentDrawer } from "client/common/hooks/stackables";
import { useFeatures } from "client/clinics/hooks";

import SubscriptionFrequencySelect from "../SubscriptionFrequencySelect";
import SubscriptionDateBanners from "../SubscriptionDateBanners";

export type SubscriptionFrequencyUpdateDrawerProps = {
  subscription: DetailSubscription | ListSubscription;
};

const SubscriptionFrequencyUpdateDrawer = ({
  subscription,
}: SubscriptionFrequencyUpdateDrawerProps) => {
  const { close } = useCurrentDrawer();
  const isEdbEnabled = useFeatures()[Feature.AutoshipEDB];
  const {
    mutate: update,
    isLoading,
    error,
  } = useUpdateSubscriptionMutation(subscription.id, {
    onSuccess: close,
  });

  const [value, setValue] = useState<SubscriptionFrequency>(
    subscription.is_active_frequency_valid
      ? subscription.active_frequency
      : subscription.recommended_frequency,
  );

  const submit = () => {
    update({
      action: UserSubscriptionAction.Modify,
      ...value,
    });
  };

  const selectedFrequency = [
    subscription.active_frequency,
    subscription.recommended_frequency,
    ...subscription.alternative_frequencies,
  ].find(
    (frequency) =>
      SubscriptionFrequencySelect.frequencyValueToString(frequency) ===
      SubscriptionFrequencySelect.frequencyValueToString(value),
  );

  return (
    <DrawerLayout>
      <DrawerLayout.Header
        title="Update frequency"
        description={`Currently receiving ${subscription.active_frequency.display.toLowerCase()}.`}
        onClose={close}
      />
      <DrawerLayout.Form
        size="l"
        submitText="Update frequency"
        canSubmit={true}
        onSubmit={submit}
        error={error}
        isLoading={isLoading}
        fieldErrors={[error?.errors.action, error?.errors.frequency, error?.errors.interval_unit]}
      >
        <DrawerLayout.Form.Group>
          <DrawerLayout.Form.Label>Select your new Autoship frequency:</DrawerLayout.Form.Label>
          <SubscriptionFrequencySelect
            subscription={subscription}
            value={value}
            onSelect={setValue}
          />
        </DrawerLayout.Form.Group>
        {selectedFrequency && (
          <DrawerLayout.Form.Group>
            <DrawerLayout.Form.Label>
              {isEdbEnabled
                ? "Your next estimated delivery dates will be:"
                : "Your next order dates will be:"}
            </DrawerLayout.Form.Label>
            <SubscriptionDateBanners
              dates={(isEdbEnabled
                ? selectedFrequency.expected_delivery_dates
                : selectedFrequency.next_order_dates
              ).map((str) => normalizeToDate(str))}
            />
          </DrawerLayout.Form.Group>
        )}
      </DrawerLayout.Form>
    </DrawerLayout>
  );
};

export default SubscriptionFrequencyUpdateDrawer;
